import { ToggleGroupItem } from '@radix-ui/react-toggle-group';
import Head from 'next/head';
import { type ReactElement, useState } from 'react';

import { caseStudies } from '@/components/Insights/caseStudies';
import CaseStudyCard from '@/components/Insights/CaseStudyCard';
import { AnimatedButton } from '@/components/Landing/AnimatedButton';
import ContactFormSheet from '@/components/Landing/ContactFormSheet';
import Layout from '@/components/Landing/Layout';
import Stack from '@/components/Landing/Stack';
import Text from '@/components/Text';
import { ToggleGroup } from '@/components/ui/toggle-group';
import { createMetaTags, JsonLd } from '@/utils/seo';

const ALL_CATEGORIES = 'All categories';

const CaseStudiesIndex = () => {
  const categories = [
    ALL_CATEGORIES,
    ...Array.from(new Set(caseStudies.map(({ category }) => category))),
  ];
  const [selectedCategory, setSelectedCategory] = useState(ALL_CATEGORIES);

  const filteredCaseStudies =
    selectedCategory === ALL_CATEGORIES
      ? caseStudies
      : caseStudies.filter(({ category }) => category === selectedCategory);

  return (
    <>
      <Head>
        {createMetaTags({
          title: 'Case Studies — In-Depth Product Partnerships | MindPal',
          description:
            'Deep dives into how MindPal builds and runs long-term product partnerships for our clients.',
        })}
        <JsonLd
          data={{
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            '@id': 'https://mindpal.co/case-studies#collectionpage',
            name: 'Case Studies - MindPal',
            url: 'https://mindpal.co/case-studies',
            publisher: { '@id': 'https://mindpal.co/#organization' },
            description:
              'In-depth case studies of long-term product partnerships built by MindPal.',
          }}
        />
      </Head>
      <Stack className="pb-10">
        <Text
          variant="display-4"
          as="h1"
          className="w-full text-jb-neutral-100"
        >
          Case Studies
        </Text>
        <ToggleGroup
          variant="purple"
          value={selectedCategory}
          onValueChange={(open) => {
            if (open) setSelectedCategory(open);
          }}
          type="single"
          className="w-full flex-wrap"
        >
          {categories.map((category) => (
            <ToggleGroupItem value={category} key={category}>
              {category}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
        <div className="grid w-full grid-cols-1 justify-stretch gap-12 pb-24 pt-6 lg:grid-cols-3 lg:gap-x-5 lg:gap-y-12">
          {filteredCaseStudies.map((caseStudy) => (
            <CaseStudyCard key={caseStudy.slug} {...caseStudy} />
          ))}
        </div>
        <div className="flex w-full flex-col items-center justify-center gap-1 py-24">
          <Text variant="display-4" className="text-center text-jb-neutral-100">
            Want a partnership like this?
          </Text>
          <Text className="pb-3 text-center text-jb-neutral-600">
            Let’s talk about what we could build together.
          </Text>
          <ContactFormSheet
            sheetTriggerProps={{
              asChild: true,
              className: 'self-center',
            }}
          >
            <AnimatedButton
              label="Get started"
              icon="material-symbols:arrow-back"
            />
          </ContactFormSheet>
        </div>
      </Stack>
    </>
  );
};

export default CaseStudiesIndex;

CaseStudiesIndex.getLayout = (page: ReactElement) => (
  <Layout theme="dark">{page}</Layout>
);
